/* ============================================================
 * bundle-builder.js
 * Drives the Bundle Builder section ("Build Your Pack").
 *  - Category tabs filter the product grid (client-side, no reload)
 *  - Per-card quantity steppers, capped at data-max-selections total
 *  - Sidebar mirrors selections: progress ring, filled slots, list
 *  - One click adds each chosen flavor to cart as its OWN real line
 *    item (one per distinct flavor, at that variant's own price) via
 *    Shopify's /cart/add.js — not one bundle product with the picks
 *    recorded as properties, so Shopify's inventory tracking sees
 *    exactly what was sold
 *  - Dispatches the theme's `cart:update` event so the cart drawer,
 *    cart icon, and cart items all update natively
 * ============================================================ */
(function () {
  'use strict';

  const PROGRESS_CIRCUMFERENCE = 2 * Math.PI * 28; // matches r=28 in the section's SVG

  // Tracks roots already wired up so a `shopify:section:load` firing for one
  // edited section doesn't re-attach duplicate listeners to every other
  // untouched instance still on the page (the theme editor swaps in an
  // entirely new root element for the section that actually changed, so this
  // only ever skips nodes that are unchanged).
  const initializedRoots = new WeakSet();

  function init(root) {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const grid = root.querySelector('[data-bb-grid]');
    const tabsWrap = root.querySelector('[data-bb-tabs]');
    const cards = Array.from(root.querySelectorAll('[data-bb-card]'));
    const slots = Array.from(root.querySelectorAll('[data-bb-slot]'));
    const selectedList = root.querySelector('[data-bb-selected-list]');
    const countEl = root.querySelector('[data-bb-count]');
    const countLabelEl = root.querySelector('[data-bb-count-label]');
    const statusEl = root.querySelector('[data-bb-status]');
    const progressRing = root.querySelector('[data-bb-progress-ring]');
    const ctaBtn = root.querySelector('[data-bb-add-to-cart]');
    const ctaLabel = root.querySelector('[data-bb-cta-label]');
    const addStatusEl = root.querySelector('[data-bb-add-status]');
    const clearBtn = root.querySelector('[data-bb-clear]');

    // Mobile-only persistent bar — mirrors the sidebar's own progress/count/
    // CTA state (see renderSidebar()) since the sidebar is a collapsed sheet
    // on mobile and this is what stays visible. All optional: none of these
    // exist unless the mobile dock markup is present, and every read below
    // is null-guarded the same way the desktop refs already are.
    const mobileDock = root.querySelector('[data-bb-mobile-dock]');
    const mobileToggle = root.querySelector('[data-bb-mobile-toggle]');
    const mobileChevron = root.querySelector('[data-bb-mobile-chevron]');
    const mobileCountEl = root.querySelector('[data-bb-mobile-count]');
    const mobileFractionEl = root.querySelector('[data-bb-mobile-fraction]');
    const mobileCountLabelEl = root.querySelector('[data-bb-mobile-count-label]');
    const mobileProgressRing = root.querySelector('[data-bb-mobile-progress-ring]');
    const mobileCtaBtn = root.querySelector('[data-bb-mobile-add-to-cart]');
    const mobileCtaLabel = root.querySelector('[data-bb-mobile-cta-label]');

    // Desktop-only floating pack summary — mirrors the sidebar's own
    // progress/count/CTA state the same way the mobile bar above does, into
    // an entirely separate set of data-bb-desktop-* elements. Never reads or
    // writes any data-bb-mobile-* ref, and vice versa. All optional/null-
    // guarded, same as the mobile refs.
    const desktopToggle = root.querySelector('[data-bb-desktop-toggle]');
    const desktopChevron = root.querySelector('[data-bb-desktop-chevron]');
    const desktopPanel = root.querySelector('[data-bb-desktop-panel]');
    const desktopCountEl = root.querySelector('[data-bb-desktop-count]');
    const desktopCountLabelEl = root.querySelector('[data-bb-desktop-count-label]');
    const desktopStatusEl = root.querySelector('[data-bb-desktop-status]');
    const desktopProgressRing = root.querySelector('[data-bb-desktop-progress-ring]');
    const desktopSlots = Array.from(root.querySelectorAll('[data-bb-desktop-slot]'));
    const desktopSelectedList = root.querySelector('[data-bb-desktop-selected-list]');
    const desktopCtaBtn = root.querySelector('[data-bb-desktop-add-to-cart]');
    const desktopCtaLabel = root.querySelector('[data-bb-desktop-cta-label]');
    const desktopAddStatusEl = root.querySelector('[data-bb-desktop-add-status]');
    const desktopClearBtn = root.querySelector('[data-bb-desktop-clear]');
    const desktopToggleCountEl = root.querySelector('[data-bb-desktop-toggle-count]');
    const desktopFractionEl = root.querySelector('[data-bb-desktop-fraction]');
    const desktopToggleCountLabelEl = root.querySelector('[data-bb-desktop-toggle-count-label]');
    const desktopToggleRing = root.querySelector('[data-bb-desktop-toggle-ring]');

    if (!grid || !cards.length || !ctaBtn) return;

    const max = parseInt(root.dataset.maxSelections, 10) || 8;
    const bundlePrice = root.dataset.bundlePrice || '';
    const samplePackLabel = root.dataset.samplePackLabel || 'Sample Pack';
    // Liquid renders the checkbox setting as the literal string "true"/
    // "false" — absent entirely (e.g. an older cached section render)
    // defaults this on, matching the setting's own schema default.
    const showCartTag = root.dataset.showCartTag !== 'false';
    const cartType = root.dataset.cartType || 'drawer';

    const labels = {
      add: root.dataset.labelAdd || 'Add — %price%',
      addIncomplete: root.dataset.labelAddIncomplete || 'Select %remaining% More',
      adding: root.dataset.labelAdding || 'Adding…',
      added: root.dataset.labelAdded || 'Added ✓',
      error: root.dataset.labelError || 'Something went wrong — try again',
      statusReady: root.dataset.labelStatusReady || 'Ready',
      statusBuilding: root.dataset.labelStatusBuilding || 'Keep Going',
      countComplete: root.dataset.labelCountComplete || 'All %max% Selected',
      countProgress: root.dataset.labelCountProgress || '%count% of %max% Selected',
    };

    if (progressRing) {
      progressRing.style.strokeDasharray = String(PROGRESS_CIRCUMFERENCE);
      progressRing.style.strokeDashoffset = String(PROGRESS_CIRCUMFERENCE);
    }
    if (mobileProgressRing) {
      mobileProgressRing.style.strokeDasharray = String(PROGRESS_CIRCUMFERENCE);
      mobileProgressRing.style.strokeDashoffset = String(PROGRESS_CIRCUMFERENCE);
    }
    if (desktopProgressRing) {
      desktopProgressRing.style.strokeDasharray = String(PROGRESS_CIRCUMFERENCE);
      desktopProgressRing.style.strokeDashoffset = String(PROGRESS_CIRCUMFERENCE);
    }
    if (desktopToggleRing) {
      desktopToggleRing.style.strokeDasharray = String(PROGRESS_CIRCUMFERENCE);
      desktopToggleRing.style.strokeDashoffset = String(PROGRESS_CIRCUMFERENCE);
    }

    /**
     * True while an add-to-cart request is in flight. Guards the grid, the
     * selected-list steppers, and Clear against mutating `unitOrder` while
     * a payload built from it is already in transit to Shopify — without
     * this, a decrease or Clear clicked mid-request would desync the
     * `cart:update` event's item count from what was actually added (the
     * real Shopify cart would still be correct; only the live badge/count
     * elsewhere in the theme would read wrong until the next page load).
     */
    let addToCartInFlight = false;

    /** Selection state: variantId -> entry (catalog order, not pick order) */
    const state = new Map();
    /**
     * One entry per SELECTED UNIT (not per flavor), in true chronological add
     * order — e.g. picking 2× Watermelon then 1× Orange is
     * ['watermelon', 'watermelon', 'orange'], index-for-index the same order
     * the sidebar slots fill in. This is deliberately NOT "one entry per
     * flavor, grouped by when that flavor was first picked" (an earlier
     * version worked that way): grouping by flavor meant incrementing an
     * EARLIER-picked flavor's quantity inserted its new unit into the middle
     * of that flavor's own block, silently reassigning every slot after it
     * to a different flavor's image — visually "the wrong product" in a
     * slot, even though cart contents were always correct. A flat
     * chronological order fixes that: a new unit always appends at the true
     * end, so no other flavor's already-placed slot ever moves.
     */
    const unitOrder = [];
    /**
     * Units currently mid-flight (added, animating, not yet committed to
     * state), as a list of cancellable tokens keyed by variant id. Tracking
     * per-id (not just a single counter) lets `pendingFor()`/`totalPending()`
     * feed into `nextDestinationIndex()` and the max-selections guard, and
     * lets a decrease clicked mid-flight
     * cancel a pending add instead of that add silently re-applying once
     * it lands (see the add/decrease branches in the grid click handler).
     */
    const pendingFlightsById = new Map();

    function pendingFor(id) {
      const list = pendingFlightsById.get(id);
      return list ? list.length : 0;
    }

    function totalPending() {
      let total = 0;
      pendingFlightsById.forEach((list) => {
        total += list.length;
      });
      return total;
    }

    // Out-of-stock variants are filtered out server-side (see the section's
    // `if variant.available` guard), so every card reaching this point is
    // purchasable — no sold-out state to track here.
    cards.forEach((card) => {
      const id = card.dataset.variantId;
      state.set(id, {
        id,
        title: card.dataset.title || '',
        category: card.dataset.category || '',
        color: card.dataset.color || '',
        image: card.dataset.image || '',
        qty: 0,
        card,
      });
    });

    function totalCount() {
      return unitOrder.length;
    }

    /**
     * Resolves `unitOrder` (ids) to their state entries, in the same true
     * chronological order the sidebar slots are filled. Shared by
     * renderSidebar() (to paint the slots) and the add-to-cart handler (to
     * list flavors in the order they were picked).
     */
    function buildSlotUnits() {
      return unitOrder.map((id) => state.get(id)).filter(Boolean);
    }

    /**
     * Distinct flavor ids, in the order each was FIRST picked (unaffected by
     * later re-increments of an already-picked flavor). Used wherever the UI
     * groups by flavor instead of by individual unit — the sidebar's
     * selected-items list (one row per flavor, with a quantity) and building
     * the cart-add payload (one line item per flavor, at its own quantity)
     * — as opposed to `buildSlotUnits()`, which is one entry per physical
     * unit/slot.
     */
    function distinctPickOrder() {
      const seen = new Set();
      const order = [];
      unitOrder.forEach((id) => {
        if (!seen.has(id)) {
          seen.add(id);
          order.push(id);
        }
      });
      return order;
    }

    function template(str, tokens) {
      return str.replace(/%(\w+)%/g, (match, key) => (key in tokens ? tokens[key] : match));
    }

    /** Matches the section's own mobile breakpoint (see bundle-builder.css)
     *  — used to skip the fly-to-slot animation on mobile while keeping it
     *  on desktop. Checked live at click time, not cached, so it still
     *  behaves correctly across a resize/orientation change. */
    function isMobileViewport() {
      try {
        return window.matchMedia('(max-width: 749px)').matches;
      } catch (e) {
        return false;
      }
    }

    /** Sets the CTA's label text on the sidebar button and its mobile-bar
     *  and desktop-bubble mirrors at once, so every state change
     *  (ready/adding/added/error) shows consistently regardless of which is
     *  visible. */
    function setCtaLabel(text) {
      if (ctaLabel) ctaLabel.textContent = text;
      if (mobileCtaLabel) mobileCtaLabel.textContent = text;
      if (desktopCtaLabel) desktopCtaLabel.textContent = text;
    }

    /** Same, for the CTA's disabled state. */
    function setCtaDisabled(disabled) {
      ctaBtn.disabled = disabled;
      if (mobileCtaBtn) mobileCtaBtn.disabled = disabled;
      if (desktopCtaBtn) desktopCtaBtn.disabled = disabled;
    }

    /**
     * Clones the card's own <img> and animates it flying — arcing up,
     * rotating, and scaling down — into the sidebar slot it's headed for,
     * then calls `done()` once it lands. Mirrors the flyClone() pattern from
     * the prior "Build Your Pack" prototype (builder_animation.js) rather
     * than the theme's simpler bezier `<fly-to-cart>` element. The caller is
     * expected to commit the actual state change inside `done()` — the
     * animation IS the add, not decoration layered on an already-applied one.
     */
    function flyClone(srcImgEl, destEl, done) {
      const s = srcImgEl && typeof srcImgEl.getBoundingClientRect === 'function' ? srcImgEl.getBoundingClientRect() : null;
      const d = destEl && typeof destEl.getBoundingClientRect === 'function' ? destEl.getBoundingClientRect() : null;

      if (!s || !d || !s.width || !s.height || !d.width || typeof srcImgEl.animate !== 'function') {
        done();
        return;
      }

      const clone = srcImgEl.cloneNode(true);
      const cx = s.left + s.width / 2;
      const cy = s.top + s.height / 2;
      const dx = d.left + d.width / 2 - cx;
      const dy = d.top + d.height / 2 - cy;
      const endScale = Math.max(0.12, (d.width * 1.4) / s.width);

      Object.assign(clone.style, {
        position: 'fixed',
        left: s.left + 'px',
        top: s.top + 'px',
        width: s.width + 'px',
        height: s.height + 'px',
        margin: '0',
        zIndex: '9999',
        pointerEvents: 'none',
        maxWidth: 'none',
        filter: 'drop-shadow(0 18px 26px rgba(0, 0, 0, 0.45))',
        transition: 'none',
      });
      document.body.appendChild(clone);

      // Flips a full 90° over the flight and lands there — the slot itself
      // stays rotated 90° once filled (see `.bundle-builder__slot.is-filled`
      // in bundle-builder.css), so the clone hands off to it seamlessly
      // instead of snapping back upright the moment it's removed.
      const anim = clone.animate(
        [
          { transform: 'translate(0, 0) rotate(0deg) scale(1)', opacity: 1, offset: 0 },
          {
            transform:
              'translate(' +
              dx * 0.5 +
              'px, ' +
              (dy * 0.5 - 40) +
              'px) rotate(45deg) scale(' +
              (1 + endScale) / 2 +
              ')',
            opacity: 1,
            offset: 0.55,
          },
          {
            transform: 'translate(' + dx + 'px, ' + dy + 'px) rotate(90deg) scale(' + endScale + ')',
            opacity: 0.85,
            offset: 1,
          },
        ],
        { duration: 560, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
      );

      const cleanup = () => {
        clone.remove();
        done();
      };
      anim.onfinish = cleanup;
      anim.oncancel = cleanup;
    }

    /**
     * A new unit always appends to the true end of `unitOrder` once it
     * commits — so its destination slot is simply the next index after
     * everything already committed AND everything else currently reserved
     * (in-flight, not yet committed) ahead of it. Flights all share the same
     * fixed duration and are never reordered relative to each other, so
     * "reserved in this order" and "commits in this order" always match.
     */
    function nextDestinationIndex() {
      return totalCount() + totalPending();
    }

    /** Small shake, used when an add is rejected because the pack is full. */
    function pulseSidebar() {
      const sidebar = root.querySelector('[data-bb-sidebar]');
      if (!sidebar || typeof sidebar.animate !== 'function') return;
      sidebar.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(-7px)' },
          { transform: 'translateX(7px)' },
          { transform: 'translateX(-4px)' },
          { transform: 'translateX(0)' },
        ],
        { duration: 320, easing: 'ease-in-out' }
      );
    }

    /** Small pop, used on a blocked add and when the pack just filled up. */
    function pulseCta() {
      if (typeof ctaBtn.animate !== 'function') return;
      ctaBtn.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }],
        { duration: 360, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    }

    /** Fade/slide-in, used when switching category tabs. */
    function animateGrid() {
      if (!grid || typeof grid.animate !== 'function') return;
      grid.animate(
        [
          { opacity: 0, transform: 'translateY(12px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 420, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    }

    function setQty(id, qty) {
      const entry = state.get(id);
      if (!entry) return;

      const nextQty = Math.max(0, qty);
      const delta = nextQty - entry.qty;

      // Block increases once the pack is full.
      if (delta > 0 && totalCount() + delta > max) return;

      if (delta > 0) {
        // Always appends at the true end — see the `unitOrder` comment above
        // for why this (and not grouping by flavor) is what keeps every
        // other flavor's already-placed slot from silently moving.
        for (let i = 0; i < delta; i++) unitOrder.push(id);
      } else if (delta < 0) {
        // Removes this flavor's own most-recently-added unit(s) — its other
        // units, and every other flavor's units, keep their slot position.
        for (let i = 0; i < -delta; i++) {
          const idx = unitOrder.lastIndexOf(id);
          if (idx !== -1) unitOrder.splice(idx, 1);
        }
      }

      entry.qty = nextQty;

      renderCard(entry);
      renderSidebar();
    }

    function renderCard(entry) {
      const { card } = entry;
      const addBtn = card.querySelector('[data-bb-add]');
      const stepper = card.querySelector('[data-bb-stepper]');
      const qtyEl = card.querySelector('[data-bb-qty]');
      const badge = card.querySelector('[data-bb-card-badge]');
      const increaseBtn = card.querySelector('[data-bb-increase]');

      const hasQty = entry.qty > 0;

      if (addBtn) {
        addBtn.hidden = hasQty;
        addBtn.disabled = totalCount() >= max;
      }
      if (stepper) stepper.hidden = !hasQty;
      if (qtyEl) qtyEl.textContent = String(entry.qty);
      if (badge) {
        badge.hidden = !hasQty;
        badge.textContent = String(entry.qty);
      }
      if (increaseBtn) increaseBtn.disabled = totalCount() >= max;
    }

    function renderSidebar() {
      const count = totalCount();
      const remaining = Math.max(0, max - count);
      const isComplete = count >= max;

      if (countEl) countEl.textContent = String(count);

      if (progressRing) {
        const offset = PROGRESS_CIRCUMFERENCE * (1 - count / max);
        progressRing.style.strokeDashoffset = String(Math.max(0, offset));
      }

      if (statusEl) statusEl.textContent = isComplete ? labels.statusReady : labels.statusBuilding;

      if (countLabelEl) {
        countLabelEl.textContent = isComplete
          ? template(labels.countComplete, { max })
          : template(labels.countProgress, { count, max });
      }

      // Mirror the same values into the mobile bar (see the refs above).
      if (mobileCountEl) mobileCountEl.textContent = String(count);
      if (mobileFractionEl) mobileFractionEl.textContent = count + '/' + max;
      if (mobileProgressRing) {
        const offset = PROGRESS_CIRCUMFERENCE * (1 - count / max);
        mobileProgressRing.style.strokeDashoffset = String(Math.max(0, offset));
      }
      if (mobileCountLabelEl) {
        mobileCountLabelEl.textContent = isComplete
          ? template(labels.countComplete, { max })
          : template(labels.countProgress, { count, max });
      }

      // Mirror the same values into the desktop floating bubble (see the refs above).
      if (desktopCountEl) desktopCountEl.textContent = String(count);
      if (desktopProgressRing) {
        const offset = PROGRESS_CIRCUMFERENCE * (1 - count / max);
        desktopProgressRing.style.strokeDashoffset = String(Math.max(0, offset));
      }
      if (desktopStatusEl) desktopStatusEl.textContent = isComplete ? labels.statusReady : labels.statusBuilding;
      if (desktopCountLabelEl) {
        desktopCountLabelEl.textContent = isComplete
          ? template(labels.countComplete, { max })
          : template(labels.countProgress, { count, max });
      }
      if (desktopToggleCountEl) desktopToggleCountEl.textContent = String(count);
      if (desktopFractionEl) desktopFractionEl.textContent = count + '/' + max;
      if (desktopToggleRing) {
        const offset = PROGRESS_CIRCUMFERENCE * (1 - count / max);
        desktopToggleRing.style.strokeDashoffset = String(Math.max(0, offset));
      }
      if (desktopToggleCountLabelEl) {
        desktopToggleCountLabelEl.textContent = isComplete
          ? template(labels.countComplete, { max })
          : template(labels.countProgress, { count, max });
      }

      // Fill slots in selection order — each unit of quantity claims one slot.
      // Shared by both the sidebar's own slots and the desktop bubble's.
      const units = buildSlotUnits();
      function paintSlots(slotEls) {
        slotEls.forEach((slot, i) => {
          const unit = units[i];
          if (unit) {
            slot.classList.add('is-filled');
            if (unit.image) {
              // Image present — show it on a transparent background, no color fill.
              slot.style.setProperty('--bb-slot-image', 'url("' + unit.image + '")');
              slot.style.removeProperty('--bb-slot-fallback-color');
            } else {
              // No image for this flavor — fall back to a flat color fill.
              slot.style.removeProperty('--bb-slot-image');
              slot.style.setProperty('--bb-slot-fallback-color', unit.color);
            }
          } else {
            slot.classList.remove('is-filled');
            slot.style.removeProperty('--bb-slot-image');
            slot.style.removeProperty('--bb-slot-fallback-color');
          }
        });
      }
      paintSlots(slots);
      paintSlots(desktopSlots);

      // Rebuild a selected-item list, in pick order. Shared by the sidebar's
      // own list and the desktop bubble's — same rows, same steppers.
      function renderSelectedListInto(listEl) {
        if (!listEl) return;
        listEl.textContent = '';
        distinctPickOrder().forEach((id) => {
          const entry = state.get(id);
          if (!entry || entry.qty < 1) return;

          const li = document.createElement('li');
          li.className = 'bundle-builder__selected-item';
          li.innerHTML =
            '<span class="bundle-builder__selected-item-dot" aria-hidden="true"></span>' +
            '<span class="bundle-builder__selected-item-info">' +
            '<span class="bundle-builder__selected-item-title"></span>' +
            (entry.category ? '<span class="bundle-builder__selected-item-category"></span>' : '') +
            '</span>' +
            '<div class="bundle-builder__stepper">' +
            '<button type="button" class="bundle-builder__stepper-btn" data-bb-decrease aria-label="Remove one ' +
            entry.title.replace(/"/g, '&quot;') +
            '">−</button>' +
            '<span class="bundle-builder__stepper-qty" data-bb-qty></span>' +
            '<button type="button" class="bundle-builder__stepper-btn bundle-builder__stepper-btn--accent" data-bb-increase aria-label="Add one more ' +
            entry.title.replace(/"/g, '&quot;') +
            '">+</button>' +
            '</div>';

          li.style.setProperty('--bb-item-color', entry.color);
          li.dataset.id = entry.id;
          li.querySelector('.bundle-builder__selected-item-title').textContent = entry.title;
          const categoryEl = li.querySelector('.bundle-builder__selected-item-category');
          if (categoryEl) categoryEl.textContent = entry.category;
          li.querySelector('[data-bb-qty]').textContent = String(entry.qty);
          li.querySelector('[data-bb-increase]').disabled = totalCount() >= max;

          listEl.appendChild(li);
        });
      }
      renderSelectedListInto(selectedList);
      renderSelectedListInto(desktopSelectedList);

      // CTA state.
      setCtaDisabled(!isComplete);
      setCtaLabel(
        isComplete ? template(labels.add, { price: bundlePrice }) : template(labels.addIncomplete, { remaining })
      );
    }

    function clearAll() {
      // `unitOrder` must be emptied BEFORE the per-card loop below —
      // renderCard() disables a card's "+ Add" button based on
      // `totalCount() >= max` (live off `unitOrder`), so clearing it after
      // the loop meant every card still saw the old (full) count while
      // being re-rendered, leaving every "+ Add" button stuck disabled
      // until a full page reload, even though the sidebar/summary reset
      // correctly (renderSidebar() already ran after this line).
      unitOrder.length = 0;
      state.forEach((entry) => {
        entry.qty = 0;
        renderCard(entry);
      });
      renderSidebar();
      if (addStatusEl) addStatusEl.textContent = '';
      if (desktopAddStatusEl) desktopAddStatusEl.textContent = '';
    }

    // ---- Card grid: add / stepper delegation ----
    grid.addEventListener('click', (event) => {
      if (addToCartInFlight) return;
      const card = event.target.closest('[data-bb-card]');
      if (!card) return;
      const id = card.dataset.variantId;
      const entry = state.get(id);

      if (event.target.closest('[data-bb-add]') || event.target.closest('[data-bb-increase]')) {
        if (!entry) return;

        // Reserve against `max` up front — pending (mid-flight) units count
        // too, so a burst of clicks before any animation lands can't push
        // the real total past the cap.
        if (totalCount() + totalPending() >= max) {
          pulseSidebar();
          pulseCta();
          return;
        }

        const destIndex = nextDestinationIndex();
        // Excludes the mountain graphic — .bundle-builder__card-image can now
        // contain two <img> tags (mountain behind, product photo on top), and
        // without this the flying clone would grab whichever is first in the
        // DOM (the mountain), not the actual product being added.
        const srcImgEl = card.querySelector('.bundle-builder__card-image img:not(.bundle-builder__card-mountain)');
        const destEl = slots[destIndex] || null;

        // A cancellable token for this specific flight — a decrease clicked
        // before it lands can flip `cancelled` so it's skipped at settle
        // time, instead of silently re-adding a unit the user just removed.
        const token = { cancelled: false };
        let list = pendingFlightsById.get(id);
        if (!list) {
          list = [];
          pendingFlightsById.set(id, list);
        }
        list.push(token);

        const settle = () => {
          const idx = list.indexOf(token);
          if (idx !== -1) list.splice(idx, 1);
          if (list.length === 0) pendingFlightsById.delete(id);

          if (token.cancelled) return;

          const wasComplete = totalCount() >= max;
          setQty(id, entry.qty + 1);
          if (!wasComplete && totalCount() >= max) pulseCta();
        };

        if (srcImgEl && destEl && !isMobileViewport()) {
          flyClone(srcImgEl, destEl, settle);
        } else {
          settle();
        }
      } else if (event.target.closest('[data-bb-decrease]')) {
        // Cancel the most recently started pending add for this flavor, if
        // any, so it doesn't land and silently re-apply right after this
        // decrease — then apply the normal decrement to committed state.
        const pending = pendingFlightsById.get(id);
        if (pending && pending.length) pending[pending.length - 1].cancelled = true;

        if (entry) setQty(id, entry.qty - 1);
      }
    });

    // ---- Selected list(s): stepper delegation ----
    // Shared by the sidebar's own list and the desktop bubble's — same
    // row markup (each <li data-id="...">), same stepper contract.
    function wireSelectedListSteppers(listEl) {
      if (!listEl) return;
      listEl.addEventListener('click', (event) => {
        if (addToCartInFlight) return;
        const row = event.target.closest('[data-id]');
        if (!row) return;
        const id = row.dataset.id;
        const entry = state.get(id);
        if (!entry) return;

        if (event.target.closest('[data-bb-increase]')) {
          // Same cap check the grid's add flow uses — without it, this
          // increment and a grid flight already in flight for a different
          // (brand-new) flavor could each pass their own guard and, once
          // both land, push the total one past `max`.
          if (totalCount() + totalPending() >= max) {
            pulseSidebar();
            pulseCta();
            return;
          }
          setQty(id, entry.qty + 1);
        } else if (event.target.closest('[data-bb-decrease]')) {
          const pending = pendingFlightsById.get(id);
          if (pending && pending.length) pending[pending.length - 1].cancelled = true;

          setQty(id, entry.qty - 1);
        }
      });
    }
    wireSelectedListSteppers(selectedList);
    wireSelectedListSteppers(desktopSelectedList);

    // ---- Tabs: filter grid ----
    if (tabsWrap) {
      tabsWrap.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-bb-tab], [data-bb-all-tab]');
        if (!tab) return;

        tabsWrap.querySelectorAll('[data-bb-tab], [data-bb-all-tab]').forEach((t) => {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });

        // The built-in "All" tab is identified by a dedicated attribute rather
        // than a reserved tag value, so a merchant-defined category tag can
        // never collide with it (e.g. a category literally tagged "all").
        const isAllTab = tab.hasAttribute('data-bb-all-tab');
        const tag = tab.dataset.bbTab;
        cards.forEach((card) => {
          const categories = (card.dataset.categories || '').split(',').filter(Boolean);
          const show = isAllTab || categories.includes(tag);
          card.hidden = !show;
        });

        animateGrid();
      });
    }

    // ---- Clear ----
    const handleClear = () => {
      if (addToCartInFlight) return;
      clearAll();
    };
    if (clearBtn) clearBtn.addEventListener('click', handleClear);
    if (desktopClearBtn) desktopClearBtn.addEventListener('click', handleClear);

    // ---- Mobile bar: expand/collapse the sidebar sheet, forward the CTA ----
    const sidebarEl = root.querySelector('[data-bb-sidebar]');
    if (mobileToggle && sidebarEl) {
      mobileToggle.addEventListener('click', () => {
        const expanded = sidebarEl.hasAttribute('data-bb-expanded');
        if (expanded) {
          sidebarEl.removeAttribute('data-bb-expanded');
        } else {
          sidebarEl.setAttribute('data-bb-expanded', '');
        }
        mobileToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (mobileChevron) mobileChevron.textContent = expanded ? '▲' : '▼';
      });
    }

    // The real add-to-cart logic lives on `ctaBtn` below — forward to it
    // rather than duplicating the cart/fetch flow for a second button.
    // `.click()` on a disabled button is a no-op, so no extra guard needed.
    if (mobileCtaBtn) mobileCtaBtn.addEventListener('click', () => ctaBtn.click());

    // ---- Mobile bar: optionally confine it to this section only ----
    // Off by default (the dock shows everywhere on mobile, as it always
    // has — e.g. the dedicated Bundle Builder page). When the "Only show
    // within this section" setting is on (meant for embedding this section
    // partway down a longer page, like near the bottom of a product page),
    // the fixed dock is only visible while the section itself is on screen —
    // hidden both above it and once the visitor scrolls past it.
    if (mobileDock && root.dataset.limitMobileDock === 'true' && 'IntersectionObserver' in window) {
      // Hidden immediately, before the observer's first callback, so a page
      // that loads already scrolled away from this section (in either
      // direction) doesn't flash the dock visible for a frame first.
      mobileDock.setAttribute('data-bb-outside-section', '');

      const sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              mobileDock.removeAttribute('data-bb-outside-section');
            } else {
              mobileDock.setAttribute('data-bb-outside-section', '');
            }
          });
        },
        { threshold: 0 }
      );
      sectionObserver.observe(root);
    }

    // ---- Desktop bubble: expand/collapse the floating panel, forward the CTA ----
    // Same pattern as the mobile toggle above, against its own panel/attribute
    // (data-bb-desktop-expanded on the panel itself, not data-bb-expanded on
    // the sidebar) so the two never interact.
    if (desktopToggle && desktopPanel) {
      desktopToggle.addEventListener('click', () => {
        const expanded = desktopPanel.hasAttribute('data-bb-desktop-expanded');
        if (expanded) {
          desktopPanel.removeAttribute('data-bb-desktop-expanded');
        } else {
          desktopPanel.setAttribute('data-bb-desktop-expanded', '');
        }
        desktopToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (desktopChevron) desktopChevron.textContent = expanded ? '▲' : '▼';
      });
    }
    if (desktopCtaBtn) desktopCtaBtn.addEventListener('click', () => ctaBtn.click());

    // ---- Add to cart ----
    ctaBtn.addEventListener('click', async () => {
      // The `addToCartInFlight` check is a second line of defense against a
      // double-submit: `ctaBtn.disabled` alone isn't enough, since an
      // external caller (root.bundleBuilderApi.addVariants, guarded
      // separately) filling the pack back up mid-request would otherwise
      // re-enable this button via the normal renderSidebar() CTA logic
      // while the first request is still in flight.
      if (ctaBtn.disabled || addToCartInFlight) return;

      // One real line item per distinct flavor, at that variant's own
      // quantity — not one bundle product with the picks recorded as
      // properties. This is what lets Shopify's own inventory tracking see
      // exactly what was sold; properties are just text, they don't touch
      // stock. See the section's own top-of-file comment for the tradeoffs.
      const items = [];
      distinctPickOrder().forEach((id) => {
        const entry = state.get(id);
        if (!entry || entry.qty < 1) return;
        const item = { id: parseInt(entry.id, 10), quantity: entry.qty };
        if (showCartTag) {
          // Plain (non-underscore) property — the theme's cart template
          // shows these to the customer, unlike an underscore-prefixed one,
          // which is how they'd see e.g. "Bundle: Sample Pack" under each
          // flavor in their cart, tying it back to this builder. Left off
          // `items` entirely (not just an empty object) when the merchant
          // has turned the tag off, so there's nothing for Shopify to show.
          item.properties = { Bundle: samplePackLabel };
        }
        items.push(item);
      });
      if (!items.length) return;

      // Snapshot now, before anything async — `totalCount()` reads live
      // state, which the grid/steppers/Clear are blocked from touching
      // once `addToCartInFlight` is true (see below), but capturing the
      // number actually being sent here is simpler than relying on that
      // guard alone to keep the two in sync.
      const addedCount = items.reduce((sum, item) => sum + item.quantity, 0);

      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });

      const payload = { items };
      if (sectionIds.length) payload.sections = sectionIds.join(',');

      const cartAddUrl =
        typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url
          ? Theme.routes.cart_add_url
          : '/cart/add.js';

      addToCartInFlight = true;
      setCtaDisabled(true);
      setCtaLabel(labels.adding);
      if (addStatusEl) addStatusEl.textContent = '';

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const responseData = await response.json();

        if (!response.ok || responseData.status) {
          throw new Error(responseData.description || responseData.message || 'Cart add failed');
        }

        setCtaLabel(labels.added);
        if (addStatusEl) addStatusEl.textContent = 'Added to cart.';

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: 'bundle-builder',
              data: {
                source: 'product-form-component',
                itemCount: addedCount,
                sections: responseData.sections || {},
              },
            },
          })
        );

        if (cartType === 'page') {
          const cartUrl =
            typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url ? Theme.routes.cart_url : '/cart';
          window.location.href = cartUrl;
          return;
        }

        // Reset the builder so the customer can start a new pack.
        setTimeout(clearAll, 1200);
      } catch (error) {
        console.error(error);
        setCtaLabel(labels.error);
        // With real per-flavor line items, Shopify's own error names the
        // specific variant that failed (e.g. sold out between page load and
        // this click) — show that instead of the generic label when we have
        // it, since it's genuinely more useful here than before.
        if (addStatusEl) addStatusEl.textContent = error.message || labels.error;
        setTimeout(renderSidebar, 2500);
      } finally {
        // Runs even on the `cartType === 'page'` early return above (a
        // `finally` always runs before control actually leaves the
        // function) — the redirect makes it moot there, but this keeps the
        // guard from ever getting stuck true if that ever changes.
        addToCartInFlight = false;
      }
    });

    // ---- Public API ----
    // A minimal, deliberately small surface for OTHER sections on the page
    // (e.g. run-them-together.js's "Pairings to Try" Add +2 buttons) to add
    // real units without reaching into `state`/`unitOrder`/etc. directly —
    // those stay private to this closure. Exposed on `root` (not `window`)
    // so it's scoped per Bundle Builder instance, same as everything else.
    root.bundleBuilderApi = {
      getState: function () {
        return { count: totalCount(), max: max };
      },
      /**
       * Adds one unit per variant id in `variantIds` (e.g. one of each flavor
       * in a pairing). All-or-nothing: if the full list wouldn't fit in the
       * remaining space, nothing is added, the sidebar still gets its usual
       * "pack full" shake/pulse feedback, and `{ ok: false, reason: 'full' }`
       * is returned so the caller can show its own messaging. Unknown
       * variant ids are silently skipped (not every id passed is guaranteed
       * to belong to this particular Bundle Builder instance).
       */
      addVariants: function (variantIds) {
        // Same reason the grid/steppers/Clear are blocked during an
        // in-flight add-to-cart request: this is a second, independent
        // entry point into `setQty`/`unitOrder`, reachable from another
        // section on the page (e.g. run-them-together.js's "Add +2"
        // buttons) with no coordination between the two. Without this, it
        // could silently add items the in-flight request will never send
        // (they'd just get wiped by the post-success reset), or — worse —
        // push the count back up to `max` and re-enable the CTA while the
        // first request is still pending.
        if (addToCartInFlight) return { ok: false, reason: 'busy' };

        const ids = (variantIds || []).map(String);
        if (totalCount() + totalPending() + ids.length > max) {
          pulseSidebar();
          pulseCta();
          return { ok: false, reason: 'full' };
        }
        ids.forEach((id) => {
          const entry = state.get(id);
          if (entry) setQty(id, entry.qty + 1);
        });
        return { ok: true };
      },
    };

    renderSidebar();
  }

  function boot() {
    document.querySelectorAll('[data-bundle-builder]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when the theme editor swaps in fresh section markup after a
  // settings/block change — without this, a customizer edit leaves the
  // rebuilt section with no listeners attached until a full page reload.
  document.addEventListener('shopify:section:load', boot);
})();
